import React, { useEffect, useRef, useState } from "react";
import { Plus, Trash2, Upload, X, Loader2 } from "lucide-react";
import { api, fmtCAD } from "@/lib/api";
import { toast } from "sonner";

const CATS = [
  { key: "refrigerators", label: "Réfrigérateurs" },
  { key: "ranges", label: "Cuisinières & Fours" },
  { key: "washers", label: "Laveuses" },
  { key: "dryers", label: "Sécheuses" },
  { key: "dishwashers", label: "Lave-vaisselle" },
  { key: "range-hoods", label: "Hottes de cuisine" },
  { key: "freezers", label: "Congélateurs" },
  { key: "other", label: "Autre" },
];
const BRANDS = ["Frigidaire", "Electrolux", "Vesta Kitchen", "GE", "Whirlpool", "LG", "Samsung", "Maytag", "Bosch", "KitchenAid", "Autre"];
const EMPTY = { title: "", brand: "Frigidaire", category_key: "refrigerators", price: "", condition: "Usagé", quantity: 1, description: "", images: [], status: "available" };

export default function AdminUsed() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef();

  const load = () => api.get("/admin/products", { params: { used: true } }).then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        urls.push(data.url);
      }
      setForm((s) => ({ ...s, images: [...s.images, ...urls] }));
      toast.success(`${urls.length} photo(s) téléversée(s)`);
    } catch (err) {
      toast.error("Échec du téléversement");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeImg = (url) => setForm((s) => ({ ...s, images: s.images.filter((u) => u !== url) }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.price) { toast.error("Titre et prix requis"); return; }
    if (!form.images.length) { toast.error("Ajoutez au moins une photo"); return; }
    setSaving(true);
    try {
      const cat = CATS.find((c) => c.key === form.category_key);
      await api.post("/admin/products", {
        title: form.title, brand: form.brand, category_key: form.category_key,
        category_label: cat ? cat.label : "Autre", price: parseFloat(form.price),
        condition: form.condition, description: form.description,
        images: form.images, image: form.images[0], used: true, in_stock: form.status !== "sold",
        status: form.status, quantity: parseInt(form.quantity) || 1, price_on_request: false, featured: false,
      });
      toast.success("Appareil usagé ajouté");
      setForm(EMPTY); setShowForm(false); load();
    } catch (err) {
      toast.error("Échec de l'enregistrement");
    } finally { setSaving(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Supprimer cet appareil usagé ?")) return;
    await api.delete(`/admin/products/${id}`);
    toast.success("Supprimé"); load();
  };

  const toggleStatus = async (p) => {
    const next = p.status === "sold" ? "available" : "sold";
    await api.patch(`/admin/products/${p.id}/status`, { status: next });
    toast.success(next === "sold" ? "Marqué comme vendu" : "Marqué comme disponible");
    load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-extrabold">Électroménagers usagés</h1>
          <p className="text-gray-500 text-sm mt-1">{items.length} appareil(s) usagé(s)</p>
        </div>
        <button onClick={() => setShowForm((v) => !v)} className="flex items-center gap-2 bg-brand-red text-white px-5 py-2.5 font-semibold text-sm hover:bg-[#b81c14]" data-testid="used-add-toggle">
          <Plus size={16} /> Ajouter un usagé
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-white border border-gray-200 p-6 mb-8 space-y-4 max-w-3xl" data-testid="used-form">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium">Titre *</label>
              <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-title-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Prix (CAD) *</label>
              <input type="number" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-price-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Marque</label>
              <select value={form.brand} onChange={(e) => setForm({ ...form, brand: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-brand-select">
                {BRANDS.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Catégorie</label>
              <select value={form.category_key} onChange={(e) => setForm({ ...form, category_key: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-category-select">
                {CATS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">État</label>
              <select value={form.condition} onChange={(e) => setForm({ ...form, condition: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-condition-select">
                <option value="Neuf">Neuf</option>
                <option value="Boîte ouverte">Boîte ouverte</option>
                <option value="Usagé">Usagé</option>
              </select>
            </div>
            <div>
              <label className="text-sm font-medium">Quantité restante</label>
              <input type="number" min="0" value={form.quantity} onChange={(e) => setForm({ ...form, quantity: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-quantity-input" />
            </div>
            <div>
              <label className="text-sm font-medium">Statut</label>
              <select value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-status-select">
                <option value="available">Disponible</option>
                <option value="sold">Vendu</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-sm font-medium">Description</label>
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 w-full border border-gray-300 px-3 py-2 text-sm" data-testid="used-description-input" />
          </div>
          <div>
            <label className="text-sm font-medium">Photos *</label>
            <input ref={fileRef} type="file" accept="image/*" multiple onChange={onUpload} className="hidden" data-testid="used-file-input" />
            <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="mt-1 flex items-center gap-2 border border-dashed border-gray-400 px-4 py-3 text-sm text-gray-600 hover:border-brand-red w-full justify-center" data-testid="used-upload-btn">
              {uploading ? <Loader2 size={16} className="animate-spin" /> : <Upload size={16} />} {uploading ? "Téléversement…" : "Téléverser des photos"}
            </button>
            {form.images.length > 0 && (
              <div className="mt-3 flex flex-wrap gap-3">
                {form.images.map((u) => (
                  <div key={u} className="relative w-20 h-20 border border-gray-200">
                    <img src={u} alt="" className="w-full h-full object-cover" />
                    <button type="button" onClick={() => removeImg(u)} className="absolute -top-2 -right-2 bg-brand-red text-white rounded-full p-0.5" data-testid="used-remove-img"><X size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>
          <button type="submit" disabled={saving} className="bg-brand-navy text-white px-6 py-2.5 font-semibold text-sm hover:bg-black disabled:opacity-50" data-testid="used-submit">
            {saving ? "Enregistrement…" : "Enregistrer l'appareil usagé"}
          </button>
        </form>
      )}

      <div className="bg-white border border-gray-200">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-gray-500 uppercase text-xs tracking-wider">
            <tr><th className="p-3">Photo</th><th className="p-3">Titre</th><th className="p-3">Marque</th><th className="p-3">Prix</th><th className="p-3">État</th><th className="p-3">Qté</th><th className="p-3">Statut</th><th className="p-3"></th></tr>
          </thead>
          <tbody data-testid="used-table">
            {items.map((p) => (
              <tr key={p.id} className="border-t border-gray-100" data-testid={`used-row-${p.id}`}>
                <td className="p-3">{p.image ? <img src={p.image} alt="" className="w-12 h-12 object-cover border border-gray-200" /> : "—"}</td>
                <td className="p-3 font-medium">{p.title}</td>
                <td className="p-3 text-gray-600">{p.brand}</td>
                <td className="p-3">{fmtCAD(p.price)}</td>
                <td className="p-3 text-gray-600">{p.condition || "—"}</td>
                <td className="p-3 text-gray-600">{p.quantity != null ? p.quantity : "—"}</td>
                <td className="p-3">
                  <button onClick={() => toggleStatus(p)} className={`px-2.5 py-1 text-xs font-semibold rounded-full ${p.status === "sold" ? "bg-black text-white" : "bg-green-100 text-green-700"}`} data-testid={`used-status-toggle-${p.id}`}>
                    {p.status === "sold" ? "Vendu" : "Disponible"}
                  </button>
                </td>
                <td className="p-3 text-right">
                  <button onClick={() => del(p.id)} className="text-brand-red hover:text-red-700" data-testid={`used-delete-${p.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={8} className="p-8 text-center text-gray-400">Aucun appareil usagé.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
